import { Controller } from "stimulus"

/**
 * RadioToggleController
 * =====================
 * Shows/hides content sections based on which radio button is selected.
 *
 * WHAT IT DOES:
 * - Links radio buttons to content sections
 * - When a radio is selected, its associated content is shown, others are hidden
 * - Can mark inputs as required only when their section is visible
 * - Provides validation to ensure required fields in visible sections are filled
 *
 * BEHAVIOR:
 * - On page load: Checks which radio is selected (if any) and shows the
 *   corresponding content section. All other content sections are hidden.
 * - When user clicks a radio button: The content section matching the radio's
 *   data-show attribute becomes visible. All other content sections are hidden.
 *   Inputs marked as requiredInput in visible sections become required; inputs
 *   in hidden sections become not required.
 * - When a content section is hidden: Any multiselect-dropdown controllers
 *   inside that section are automatically cleared (selections reset).
 * - When validateOnSubmit is called (e.g., before form submission): Checks if
 *   any required inputs in the visible section are empty. If so, prevents the
 *   event and shows an error state on the content section.
 *
 * HTML USAGE:
 *   <form data-controller="radio-toggle">
 *
 *     <!-- Radio buttons with data-show pointing to content section IDs -->
 *     <label>
 *       <input type="radio" name="choice" value="a"
 *              data-radio-toggle-target="radio"
 *              data-show="section-a"
 *              data-action="change->radio-toggle#toggle">
 *       Option A
 *     </label>
 *
 *     <label>
 *       <input type="radio" name="choice" value="b"
 *              data-radio-toggle-target="radio"
 *              data-show="section-b"
 *              data-action="change->radio-toggle#toggle">
 *       Option B
 *     </label>
 *
 *     <!-- Radio with data-show="" means "show nothing" (default/no content) -->
 *     <label>
 *       <input type="radio" name="choice" value="none" checked
 *              data-radio-toggle-target="radio"
 *              data-show=""
 *              data-action="change->radio-toggle#toggle">
 *       None
 *     </label>
 *
 *     <!-- Content sections with data-toggle-id matching the radio's data-show -->
 *     <div data-radio-toggle-target="content"
 *          data-toggle-id="section-a"
 *          class="d-none">
 *       <p>Content for Option A</p>
 *       <!-- This input becomes required when section-a is visible -->
 *       <input type="text" data-radio-toggle-target="requiredInput">
 *     </div>
 *
 *     <div data-radio-toggle-target="content"
 *          data-toggle-id="section-b"
 *          class="d-none">
 *       <p>Content for Option B</p>
 *     </div>
 *   </form>
 *
 * CONNECTING WITH FORM-SUBMIT FOR VALIDATION:
 *   <!-- On the form element, add the form-submit:before listener -->
 *   <form data-controller="radio-toggle"
 *         data-action="submit->form-submit#handleSubmit form-submit:before->radio-toggle#validateOnSubmit">
 *
 * TARGETS:
 *   - radio: Each radio button (should have data-show attribute)
 *   - content: Each content section (should have data-toggle-id attribute)
 *   - requiredInput: Inputs that should be required when their section is visible
 *
 * VALUES (all optional):
 *   - hiddenClass: CSS class for hidden elements (default: "d-none")
 *   - errorClass: CSS class for error state (default: "has-error")
 *   - errorDuration: How long to show error state in ms (default: 3000)
 *
 * ACTIONS (usable from HTML):
 *   - toggle: Updates visibility when radio changes
 *   - validateOnSubmit: Validates and prevents form submission if invalid
 */
export default class extends Controller {
  static targets = ["radio", "content", "requiredInput"]
  static values = {
    hiddenClass: String,
    errorClass: String,
    errorDuration: Number
  }

  get hiddenClass() {
    return this.hasHiddenClassValue ? this.hiddenClassValue : "d-none"
  }

  get errorClass() {
    return this.hasErrorClassValue ? this.errorClassValue : "has-error"
  }

  get errorDuration() {
    return this.hasErrorDurationValue ? this.errorDurationValue : 3000
  }
  
  // Called when controller connects. Shows/hides content based on initial radio state.
  connect() {
    this.updateVisibility()
  }

  // Updates visibility when a radio button changes.
  toggle() {
    this.updateVisibility()
  }

  // Shows content matching the selected radio's data-show, hides all others.
  // Also updates required state on inputs.
  updateVisibility() {
    const checkedRadio = this.radioTargets.find(radio => radio.checked)
    const showId = checkedRadio ? checkedRadio.dataset.show : ""

    this.contentTargets.forEach(content => {
      const toggleId = content.dataset.toggleId
      // Content is visible if its toggleId matches showId AND showId is not empty
      const isVisible = toggleId === showId && showId !== ""
      const wasVisible = !content.classList.contains(this.hiddenClass)

      if (isVisible) {
        content.classList.remove(this.hiddenClass)
      } else {
        content.classList.add(this.hiddenClass)
        // When hiding, clear any multiselect-dropdown controllers inside
        if (wasVisible) {
          this.clearMultiselectDropdowns(content)
        }
      }

      // Update required state for inputs in this content block
      // Inputs are only required when their section is visible
      const inputs = content.querySelectorAll('[data-radio-toggle-target="requiredInput"]')
      inputs.forEach(input => {
        input.required = isVisible
      })
    })
  }

  // Clears any multiselect-dropdown controller on the given element
  clearMultiselectDropdowns(container) {
    const controller = this.application.getControllerForElementAndIdentifier(
      container,
      "multiselect-dropdown"
    )
    if (controller && typeof controller.clear === "function") {
      controller.clear()
    }
  }

  // Action handler for form-submit:before event.
  // Validates required inputs and prevents submission if invalid.
  // Triggered by: data-action="form-submit:before->radio-toggle#validateOnSubmit"
  validateOnSubmit(event) {
    if (!this.validateRequired()) {
      event.preventDefault()
      this.showValidationError()
    }
  }

  // Validates that all required inputs in the visible content section are filled.
  validateRequired() {
    const checkedRadio = this.radioTargets.find(radio => radio.checked)
    const showId = checkedRadio ? checkedRadio.dataset.show : ""

    // If no content is shown, validation passes
    if (showId === "") return true

    const visibleContent = this.getActiveContent()
    if (!visibleContent) return true

    // Check all required inputs in the visible content
    const requiredInputs = visibleContent.querySelectorAll('[data-radio-toggle-target="requiredInput"]')
    for (const input of requiredInputs) {
      if (!input.value || input.value.trim() === "") {
        return false
      }
    }

    return true
  }

  // Returns the currently visible content element, or null if none.
  getActiveContent() {
    const checkedRadio = this.radioTargets.find(radio => radio.checked)
    const showId = checkedRadio ? checkedRadio.dataset.show : ""

    if (showId === "") return null

    return this.contentTargets.find(
      content => content.dataset.toggleId === showId
    ) || null
  }

  // Shows error state on the active content element temporarily.
  showValidationError() {
    const activeContent = this.getActiveContent()
    if (activeContent) {
      activeContent.classList.add(this.errorClass)
      setTimeout(() => {
        activeContent.classList.remove(this.errorClass)
      }, this.errorDuration)
    }
  }
}
